import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './AdminOrderDetails.css';
import {
    getAllOrders,
    approveOrder,
    syncPaymongoPayment,
    updateOrderStatus,
    ORDER_STATUS,
    APPROVAL_EXPIRY_MS,
    writeNotification,
} from '../../firebase/orders';
import { useAuth } from '../../contexts/AuthContext';
import { collection, getDoc, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db, functions, isFirebaseConfigured } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import ShirtPreviewModal from '../ShirtPreviewModal/ShirtPreviewModal';
import { getRoleLabel, normalizeUserRole } from '../../utils/adminRoles';
import { getErrorMessage } from '../../utils/errorUtils';

const LAST_SELECTED_ORDER_ID_KEY = 'admin:last-selected-order-id';

// ─── Status config ─────────────────────────────────────────────────────────
const STATUS_CONFIG = {
    'pending':                                    { bg: '#fff8e1', text: '#b07c00', label: 'Pending Approval' },
    [ORDER_STATUS.PENDING_APPROVAL]:              { bg: '#fff8e1', text: '#b07c00', label: 'Pending Approval' },
    [ORDER_STATUS.APPROVED]:                      { bg: '#e3f0ff', text: '#1a5faa', label: 'Approved' },
    [ORDER_STATUS.AWAITING_PAYMENT]:              { bg: '#f3e5f5', text: '#7b1fa2', label: 'Awaiting Payment' },
    [ORDER_STATUS.IN_PRODUCTION]:                 { bg: '#e6f4ea', text: '#2d7a3a', label: 'In Production' },
    [ORDER_STATUS.TO_BE_DELIVERED]:               { bg: '#fff3e0', text: '#e65100', label: 'To Be Delivered' },
    [ORDER_STATUS.DELIVERED]:                     { bg: '#e8f5e9', text: '#1b5e20', label: 'Delivered' },
    [ORDER_STATUS.CANCELLED]:                     { bg: '#fdecea', text: '#b71c1c', label: 'Cancelled' },
    'completed':                                  { bg: '#e8f5e9', text: '#1b5e20', label: 'Delivered' },
    'processing':                                 { bg: '#e6f4ea', text: '#2d7a3a', label: 'In Production' },
    'in production':                              { bg: '#e6f4ea', text: '#2d7a3a', label: 'In Production' },
};

const CANCELLATION_REASONS = [
    'Approval window expired — customer did not check out within 72 hours.',
    'Payment not received.',
    'Insufficient stock.',
    'Cancelled by admin.',
    'Other',
];

// ─── Pricing tables ──────────────────────────────────────────────────────────
const SHIRT_SIZE_PRICES = {
    'Blue Corner White T-Shirt':        { xs:150, small:150, medium:150, large:160, xl:165, xxl:170, xxxl:175, xxxxl:180, xxxxxl:200 },
    'Blue Corner Colored T-Shirt':      { xs:180, small:180, medium:180, large:195, xl:195, xxl:205, xxxl:210, xxxxl:220, xxxxxl:235 },
    'Yalex White T-Shirt':              { xs:120, small:120, medium:120, large:120, xl:125, xxl:130, xxxl:135, xxxxl:null, xxxxxl:140 },
    'Yalex Colored T-Shirt':            { xs:125, small:125, medium:125, large:125, xl:130, xxl:135, xxxl:140, xxxxl:null, xxxxxl:150 },
    'Pro Club Inspire White T-Shirt':   { xs:null, small:200, medium:200, large:200, xl:220, xxl:230, xxxl:null, xxxxl:null, xxxxxl:null },
    'Pro Club Inspire Colored T-Shirt': { xs:null, small:250, medium:250, large:250, xl:260, xxl:270, xxxl:null, xxxxl:null, xxxxxl:null },
    'Blackhorse White T-Shirt':         { xs:120, small:120, medium:120, large:120, xl:125, xxl:130, xxxl:null, xxxxl:null, xxxxxl:null },
    'Blackhorse Colored T-Shirt':       { xs:125, small:125, medium:125, large:125, xl:130, xxl:135, xxxl:null, xxxxl:null, xxxxxl:null },
    'Active Life White T-Shirt':        { xs:120, small:120, medium:120, large:120, xl:125, xxl:130, xxxl:135, xxxxl:null, xxxxxl:140 },
    'Active Life Colored T-Shirt':      { xs:125, small:125, medium:125, large:125, xl:130, xxl:135, xxxl:140, xxxxl:null, xxxxxl:150 },
    'Drift White T-Shirt':              { xs:85,  small:85,  medium:85,  large:85,  xl:90,  xxl:95,  xxxl:null, xxxxl:null, xxxxxl:null },
    'Drift Colored T-Shirt':            { xs:95,  small:95,  medium:95,  large:95,  xl:105, xxl:115, xxxl:null, xxxxl:null, xxxxxl:null },
};
const IMPRINT_RECT = {
    'Full Front':150,'Medium Front':120,'Center Chest':50,'Across Chest':80,
    'Left Chest':50,'Right Chest':50,'Left Vertical':120,'Right Vertical':120,
    'Bottom Left':50,'Bottom Right':50,'Full Back':150,'Medium Back':120,
    'Locker Center Patch':50,'Across Shoulder':80,'Left Sleeve':40,'Right Sleeve':40,
};
const IMPRINT_CIRC = {
    'Full Front':120,'Medium Front':80,'Center Chest':50,'Across Chest':80,
    'Left Chest':40,'Right Chest':40,'Left Vertical':100,'Right Vertical':100,
    'Bottom Left':50,'Bottom Right':50,'Full Back':120,'Medium Back':80,
    'Locker Center Patch':50,'Back Center':80,'Left Sleeve':40,'Right Sleeve':40,
};

function getCaseInsensitivePrice(sizePrices, sz, fallbackPrice) {
    if (!sizePrices) return Number(fallbackPrice) || 0;
    const lowerSz = (sz || '').toLowerCase();
    const matchedKey = Object.keys(sizePrices).find(
        (key) => (key || '').toLowerCase() === lowerSz
    );
    return matchedKey ? (Number(sizePrices[matchedKey]) || Number(fallbackPrice) || 0) : (Number(fallbackPrice) || 0);
}

function isGenericProduct(item) {
    const sub = item?.subcategory;
    if (!sub) return false;
    return sub !== 'T-Shirts' && sub !== 'Jersey' && sub !== 'Polo Shirt';
}

// FIX: Include colorSurcharge per unit in shirt subtotal (was missing before)
function calcShirtSubtotal(item) {
    if (isGenericProduct(item)) {
        return (Number(item.quantity) || 0) * (Number(item.price) || 0);
    }
    
    const quantities     = item.quantities || {};
    const sizePrices     = item.sizePrices && Object.keys(item.sizePrices).length > 0
        ? item.sizePrices
        : (SHIRT_SIZE_PRICES[item.type] || {});
    const colorSurcharge = Number(item.colorSurcharge || 0);
    
    if (Object.keys(quantities).length > 0) {
        return Object.entries(quantities).reduce(
            (s, [sz, q]) => s + (q || 0) * (getCaseInsensitivePrice(sizePrices, sz, item.price) + colorSurcharge),
            0
        );
    }
    
    let totalQty = Number(item.quantity) || 0;
    return totalQty * ((Number(item.price) || 0) + colorSurcharge);
}

function calcPrintSubtotal(item) {
    if (isGenericProduct(item)) return 0;
    const quantities   = item.quantities || {};
    let totalQty       = Object.values(quantities).reduce((s, q) => s + (q || 0), 0);
    if (totalQty === 0) totalQty = Number(item.quantity) || 0;
    
    const shape        = (item.designShape || '').toLowerCase() === 'circle' ? 'circle' : 'rectangle';
    const table        = shape === 'circle' ? IMPRINT_CIRC : IMPRINT_RECT;
    const locs         = (item.printLocation || '').split(',').map(s => s.trim()).filter(Boolean);
    const prices       = locs.map(l => table[l] || 0).sort((a, b) => b - a);
    const imprintPerPc = prices.reduce((s, p, i) => s + (i === 0 ? p : Math.round(p * 0.5)), 0);
    return imprintPerPc * totalQty;
}

// FIX: Use sizePrices[size] × qty (not flat item.price × totalQty).
// sizePrices is persisted on every order item via createOrder.
// Falls back to item.price × totalQty for legacy orders created before this fix.
function calcPoloJerseyItemTotal(item) {
    const quantities = item.quantities || {};
    let totalQty   = Object.values(quantities).reduce((s, q) => s + (q || 0), 0);
    if (totalQty === 0) totalQty = Number(item.quantity) || 0;
    if (totalQty === 0) return 0;

    const sizePrices    = item.sizePrices || {};
    const hasSizePrices = Object.keys(sizePrices).length > 0 && Object.keys(quantities).length > 0;

    if (hasSizePrices) {
        const calculatedTotal = Object.entries(quantities).reduce(
            (s, [size, q]) => s + (q || 0) * getCaseInsensitivePrice(sizePrices, size, item.price),
            0
        );
        if (calculatedTotal > 0) return calculatedTotal;
    }
    // Legacy fallback for orders that predate the sizePrices fix
    if (Number(item.itemTotal) > 0) return Number(item.itemTotal);
    return totalQty * (Number(item.price) || 0);
}

function calcItemTotal(item) {
    if (isGenericProduct(item)) {
        return (Number(item.quantity) || 0) * (Number(item.price) || 0);
    }
    if (item.subcategory === 'Polo Shirt' || item.subcategory === 'Jersey') {
        return calcPoloJerseyItemTotal(item);
    }
    return calcShirtSubtotal(item) + calcPrintSubtotal(item);
}

const SIZE_LABELS = { xs:'XS', small:'Small', medium:'Medium', large:'Large', xl:'XL', xxl:'2XL', xxxl:'3XL', xxxxl:'4XL', xxxxxl:'5XL' };
const SIZE_ORDER  = ['xxs','xs','small','medium','large','xl','xxl','xxxl','xxxxl','xxxxxl'];
function toJsDate(value) {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value?.toDate === 'function') {
        const date = value.toDate();
        return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
    }
    if (typeof value?.seconds === 'number') {
        const date = new Date(value.seconds * 1000);
        return Number.isNaN(date.getTime()) ? null : date;
    }
    if (typeof value?._seconds === 'number') {
        const date = new Date(value._seconds * 1000);
        return Number.isNaN(date.getTime()) ? null : date;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
function formatDate(value) {
    if (!value) return '—';
    const d = toJsDate(value);
    if (!d) return String(value);
    return d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'Asia/Manila' });
}
function formatDateTime(value) {
    if (!value) return '—';
    const d = toJsDate(value);
    if (!d) return String(value);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function formatAddress(addr) {
    if (!addr) return null;
    if (typeof addr === 'string') return addr;
    const parts    = [addr.houseNo, addr.barangay, addr.city, addr.province, addr.zipCode].filter(Boolean).join(', ');
    const landmark = addr.landmark ? ` (Near: ${addr.landmark})` : '';
    return `${parts}${landmark}` || null;
}
function getExpiryInfo(order) {
    if (order?.status !== ORDER_STATUS.APPROVED || !order?.expiresAt) return null;
    const diff = new Date(order.expiresAt).getTime() - Date.now();
    if (diff <= 0) return { expired: true, label: 'Expired' };
    const hours   = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    return { expired: false, label: `${hours}h ${minutes}m remaining` };
}
function normalizeOrderItems(order) {
    if (Array.isArray(order?.orderItems) && order.orderItems.length > 0) return order.orderItems;
    return [];
}
function getOrderUserId(order) {
    return String(order?.userId || order?.uid || order?.customerId || '').trim();
}
function getPaymentMethodLabel(paymentMethod) {
    return String(paymentMethod || '').trim() || 'PayMongo';
}

function formatReceiptAmount(amount) {
    const value = Number(amount || 0);
    return `₱ ${value.toLocaleString()}`;
}

function formatPaymongoPaymentStatus(order) {
    const status = String(order?.paymentStatus || '').toLowerCase();
    if (status === 'paid' || order?.paymentConfirmed) return 'Paid';
    if (status === 'failed') return 'Failed';
    if (status === 'not_required') return 'Not required';
    return 'Pending';
}
function buildFullName(p) { return `${p?.firstName || ''} ${p?.lastName || ''}`.trim(); }
function normalizeText(v) { return String(v || '').trim().toLowerCase().replace(/\s+/g, ' '); }
function getOrderDateTimestamp(order) {
    if (!order) return Infinity;
    if (typeof order.date === 'string' && order.date.trim()) {
        const p = new Date(order.date);
        if (!isNaN(p.getTime())) return p.getTime();
    }
    const createdAtDate = toJsDate(order.createdAt);
    if (createdAtDate) return createdAtDate.getTime();
    return Infinity;
}

// ─── Design Email Notice Banner ───────────────────────────────────────────────
function DesignEmailNoticeBanner({ item, customerEmail }) {
    if (!item.designEmailSent) return null;

    const storeEmail = item.storeEmail || '(store email not on record)';
    const custEmail  = customerEmail || '(customer email unavailable)';

    return (
        <div className="aod-design-email-banner" role="note">
            <div className="aod-design-email-banner-icon">📧</div>
            <div className="aod-design-email-banner-text">
                <strong>Design Submission via Email</strong>
                <p>
                    Customer indicated they will send their design via email to{' '}
                    <strong>{storeEmail}</strong>.{' '}
                    Watch your inbox for a message from{' '}
                    <strong>{custEmail}</strong>.
                </p>
                {item.designEmailSentAt && (
                    <span className="aod-design-email-banner-time">
                        Sent at: {formatDateTime(
                            item.designEmailSentAt?.toDate?.()
                                ? item.designEmailSentAt.toDate()
                                : item.designEmailSentAt
                        )}
                    </span>
                )}
            </div>
        </div>
    );
}

export default function AdminOrderDetails() {
    const { currentUser, getUserProfile, userRole } = useAuth();
    const location              = useLocation();
    const navigate              = useNavigate();
    const orderFromState        = location.state?.orderData || null;

    const [orders,                setOrders]                = useState([]);
    const [selectedOrder,         setSelectedOrder]         = useState(null);
    const [isLoading,             setIsLoading]             = useState(true);
    const [customerProfile,       setCustomerProfile]       = useState(null);
    const [lightbox,              setLightbox]              = useState(null);
    const [receiptModal,          setReceiptModal]          = useState(null);
    const [confirmDialog,         setConfirmDialog]         = useState(null);
    const [cancelReason,          setCancelReason]          = useState('');
    const [cancelOther,           setCancelOther]           = useState('');
    const [actionLoading,         setActionLoading]         = useState(false);
    const [actionError,           setActionError]           = useState(null);
    const [expiryInfo,            setExpiryInfo]            = useState(null);
    const [estimatedDeliveryDate, setEstimatedDeliveryDate] = useState('');
    const [currentActorName,      setCurrentActorName]      = useState('');

    // ── ShirtPreviewModal state ──────────────────────────────────────────────
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewItem,      setPreviewItem]      = useState(null);

    const openDesignPreview = (item) => {
        setPreviewItem(item);
        setPreviewModalOpen(true);
    };

    // ── Load orders ──────────────────────────────────────────────────────────
    useEffect(() => {
        let mounted = true;
        (async () => {
            setIsLoading(true);
            const { orders: fetched } = await getAllOrders();
            if (!mounted) return;
            const sorted     = [...fetched].sort((a, b) => getOrderDateTimestamp(a) - getOrderDateTimestamp(b));
            const savedId    = localStorage.getItem(LAST_SELECTED_ORDER_ID_KEY);
            const savedOrder = savedId ? sorted.find(o => o.orderId === savedId) : null;
            setOrders(sorted);
            setSelectedOrder(orderFromState || savedOrder || sorted[0] || null);
            setIsLoading(false);
        })();
        return () => { mounted = false; };
    }, [orderFromState]);

    const order            = useMemo(() => selectedOrder || null, [selectedOrder]);
    const orderItems       = normalizeOrderItems(order);
    const itemsSubtotal    = orderItems.reduce((s, item) => s + calcItemTotal(item), 0);
    const deliveryFee      = Number(order?.deliveryFee || 0);
    const discountAmount   = Number(order?.discountAmount || 0);
    const payableOrderTotal = Math.max(0, itemsSubtotal + deliveryFee - discountAmount);

    // ── Load customer profile ────────────────────────────────────────────────
    useEffect(() => {
        let mounted = true;
        (async () => {
            const uid = getOrderUserId(order);
            if (uid) {
                const profile = await getUserProfile(uid);
                if (mounted) setCustomerProfile(profile || null);
                return;
            }
            if (!isFirebaseConfigured || !db || !order?.customerName) {
                if (mounted) setCustomerProfile(null);
                return;
            }
            try {
                const snap   = await getDocs(collection(db, 'users'));
                const target = normalizeText(order.customerName);
                let match    = null;
                snap.forEach(d => {
                    if (!match && normalizeText(buildFullName(d.data())) === target) {
                        match = { id: d.id, ...d.data() };
                    }
                });
                if (mounted) setCustomerProfile(match);
            } catch { if (mounted) setCustomerProfile(null); }
        })();
        return () => { mounted = false; };
    }, [getUserProfile, order]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            if (!currentUser?.uid || !db) {
                if (mounted) setCurrentActorName('');
                return;
            }
            try {
                const adminSnap = await getDoc(doc(db, 'admins', currentUser.uid));
                const adminName = adminSnap.exists() ? String(adminSnap.data()?.adminName || '').trim() : '';
                const fallbackName = currentUser.email ? currentUser.email.split('@')[0] : '';
                if (mounted) setCurrentActorName(adminName || currentUser.displayName || fallbackName);
            } catch {
                const fallbackName = currentUser?.email ? currentUser.email.split('@')[0] : '';
                if (mounted) setCurrentActorName(currentUser?.displayName || fallbackName);
            }
        })();
        return () => { mounted = false; };
    }, [currentUser]);

    // ── Expiry countdown ──────────────────────────────────────────────────────
    useEffect(() => {
        setExpiryInfo(getExpiryInfo(order));
        if (order?.status !== ORDER_STATUS.APPROVED || !order?.expiresAt) return;
        const timer = setInterval(() => setExpiryInfo(getExpiryInfo(order)), 60000);
        return () => clearInterval(timer);
    }, [order]);

    const customerEmail   = customerProfile?.email         || order?.customerEmail || '—';
    const customerPhone   = customerProfile?.contactNumber || customerProfile?.phoneNumber || '—';
    const deliveryAddress = formatAddress(order?.deliveryAddress) ||
        formatAddress(customerProfile?.savedAddresses?.find(a => a.isDefault) || customerProfile?.savedAddresses?.[0]) ||
        'No delivery address on file';
    const actorInfo = useMemo(() => ({
        uid: currentUser?.uid || '',
        email: currentUser?.email || '',
        role: normalizeUserRole(userRole),
        name: currentActorName || currentUser?.email || '',
    }), [currentActorName, currentUser?.email, currentUser?.uid, userRole]);
    const paymongoPaid = order?.paymentConfirmed || String(order?.paymentStatus || '').toLowerCase() === 'paid';
    const canViewReceipt = Boolean(order?.paymentReceiptId || paymongoPaid);

    const handleViewPaymentReceipt = async () => {
        if (!order?.firestoreId || !functions) {
            setActionError('Payment receipt is not available yet.');
            return;
        }

        setReceiptModal({ loading: true, receipt: null, error: '' });
        try {
            const getReceipt = httpsCallable(functions, 'getPaymentReceipt');
            let receipt = null;
            try {
                const { data } = await getReceipt({ orderFirestoreId: order.firestoreId });
                receipt = data?.receipt || null;
            } catch (_fetchErr) {
                if (paymongoPaid) {
                    const sync = httpsCallable(functions, 'syncPaymongoPayment');
                    await sync({ orderFirestoreId: order.firestoreId });
                    const { data } = await getReceipt({ orderFirestoreId: order.firestoreId });
                    receipt = data?.receipt || null;
                }
            }
            setReceiptModal({
                loading: false,
                receipt,
                error: receipt ? '' : 'Payment receipt not found yet.',
            });
        } catch (err) {
            setReceiptModal({
                loading: false,
                receipt: null,
                error: err?.message || 'Payment receipt is not available yet.',
            });
        }
    };

    const handleSyncPaymongoPayment = async () => {
        if (!order?.firestoreId) return;
        setActionLoading(true);
        const { error, paid, receiptNumber } = await syncPaymongoPayment(order.firestoreId);
        setActionLoading(false);
        if (error) {
            setActionError('Payment sync failed: ' + error);
            return;
        }
        if (paid) {
            applyStatusUpdate(ORDER_STATUS.IN_PRODUCTION, {
                paymentConfirmed: true,
                paymentStatus: 'paid',
                paymentReceiptNumber: receiptNumber || order?.paymentReceiptNumber || '',
            });
        } else {
            setActionError('PayMongo has not marked this order as paid yet. Ask the customer to finish checkout or try again shortly.');
        }
    };

    const sc         = STATUS_CONFIG[order?.status] || { bg: '#eee', text: '#555', label: order?.status || '—' };
    const currentIdx = useMemo(() => orders.findIndex(o => o.orderId === order?.orderId), [orders, order?.orderId]);
    const hasPrev    = currentIdx > 0;
    const hasNext    = currentIdx > -1 && currentIdx < orders.length - 1;

    const goToOrder = useCallback((o) => {
        setSelectedOrder(o);
        if (o?.orderId) localStorage.setItem(LAST_SELECTED_ORDER_ID_KEY, o.orderId);
    }, []);

    const handleMessageCustomer = () => {
        const userId = getOrderUserId(order) || String(customerProfile?.uid || customerProfile?.id || '').trim();
        if (!userId) {
            setActionError('Cannot open chat for this order because no customer account ID is linked yet.');
            return;
        }
        navigate('/admin/chats', {
            state: {
                openChatUserId:   userId,
                openChatUserName: order?.customerName || (customerProfile ? buildFullName(customerProfile) : 'Customer'),
            }
        });
    };

    const getOpts = useCallback(() => ({
        userId:        getOrderUserId(order),
        customerName:  order?.customerName || '',
        customerEmail: customerEmail !== '—' ? customerEmail : '',
        orderId:       order?.orderId || '',
        orderTotal:    payableOrderTotal,
    }), [order, customerEmail, payableOrderTotal]);

    const applyStatusUpdate = useCallback((newStatus, extra = {}) => {
        const updated = { ...order, status: newStatus, ...extra };
        setOrders(prev => prev.map(o => o.orderId === order.orderId ? updated : o));
        setSelectedOrder(updated);
        setConfirmDialog(null);
    }, [order]);

    const handleApprove = async () => {
        if (!order?.firestoreId) return;
        setActionLoading(true);
        try {
            const { error } = await approveOrder(order.firestoreId, { ...getOpts(), performedBy: actorInfo });
            if (error) { setActionError(getErrorMessage(error, 'Failed to approve.')); setActionLoading(false); return; }
            applyStatusUpdate(ORDER_STATUS.APPROVED, {
                expiresAt: new Date(Date.now() + APPROVAL_EXPIRY_MS).toISOString(),
                approvedBy: actorInfo,
                approvedAt: new Date().toISOString(),
            });
        } catch (error) {
            console.error('Error approving order:', error);
            setActionError('An error occurred while approving the order');
        } finally {
            setActionLoading(false);
        }
    };

    const handleMarkToBeDelivered = async () => {
        if (!order?.firestoreId) return;
        const dateToUse = estimatedDeliveryDate.trim() || order?.estimatedDeliveryDate || '';
        setActionLoading(true);
        const { error } = await updateOrderStatus(order.firestoreId, ORDER_STATUS.TO_BE_DELIVERED, {
            ...getOpts(),
            performedBy: actorInfo,
            note: 'Marked ready for delivery by admin',
            estimatedDeliveryDate: dateToUse,
        });
        setActionLoading(false);
        if (error) { setActionError(getErrorMessage(error, 'Failed to update.')); return; }
        applyStatusUpdate(ORDER_STATUS.TO_BE_DELIVERED, { estimatedDeliveryDate: dateToUse });
        setEstimatedDeliveryDate('');
    };

    const handleMarkDelivered = async () => {
        if (!order?.firestoreId) return;
        setActionLoading(true);
        const { error } = await updateOrderStatus(order.firestoreId, ORDER_STATUS.DELIVERED, { ...getOpts(), performedBy: actorInfo, note: 'Marked delivered by admin' });
        setActionLoading(false);
        if (error) { setActionError(getErrorMessage(error, 'Failed to update.')); return; }
        applyStatusUpdate(ORDER_STATUS.DELIVERED);
    };

    const handleCancel = useCallback(async () => {
        if (!order?.firestoreId) return;
        const reason = cancelReason === 'Other' ? cancelOther : cancelReason;
        if (!reason.trim()) { setActionError('Please select a cancellation reason.'); return; }
        setActionLoading(true);
        const { error } = await updateOrderStatus(order.firestoreId, ORDER_STATUS.CANCELLED, { ...getOpts(), performedBy: actorInfo, cancellationReason: reason });
        setActionLoading(false);
        if (error) { setActionError(getErrorMessage(error, 'Failed to cancel.')); return; }
        applyStatusUpdate(ORDER_STATUS.CANCELLED, { cancellationReason: reason });
        setCancelReason('');
        setCancelOther('');
    }, [order, cancelReason, cancelOther, getOpts, applyStatusUpdate, actorInfo]);

    const handleDownload = async (url, loc) => {
        try {
            const res  = await fetch(url);
            const blob = await res.blob();
            const a    = document.createElement('a');
            a.href     = URL.createObjectURL(blob);
            a.download = `${order?.orderId || 'design'}-${loc.replace(/\s+/g, '-')}.png`;
            a.click();
            URL.revokeObjectURL(a.href);
        } catch { window.open(url, '_blank'); }
    };

    const actionBtn = (() => {
        const paymentMethodLabel = getPaymentMethodLabel(order?.paymentMethod);
        switch (order?.status) {
            case 'pending':
            case ORDER_STATUS.PENDING_APPROVAL:
                return { label: 'Approve Order', color: '#1e7e34', disabled: false,
                    onClick: () => setConfirmDialog({ type: 'approve', label: `Approve order ${order.orderId}?`, onConfirm: handleApprove }) };
            case ORDER_STATUS.AWAITING_PAYMENT:
                if (paymongoPaid) {
                    return { label: 'PayMongo Payment Confirmed', color: '#7b1fa2', disabled: true, onClick: null };
                }
                return { label: 'Sync PayMongo Payment', color: '#7b1fa2', disabled: false,
                    onClick: () => setConfirmDialog({ type: 'payment', label: `Sync PayMongo payment for ${order.orderId}?`, onConfirm: handleSyncPaymongoPayment }) };
            case ORDER_STATUS.IN_PRODUCTION:
            case 'processing':
                return { label: 'Mark as To Be Delivered', color: '#e65100',
                    onClick: () => setConfirmDialog({ type: 'deliver', label: `Mark order ${order.orderId} as ready for delivery?`, onConfirm: handleMarkToBeDelivered }) };
            case ORDER_STATUS.TO_BE_DELIVERED:
                return { label: 'Mark as Delivered', color: '#1b5e20', disabled: false,
                    onClick: () => setConfirmDialog({ type: 'delivered', label: `Mark order ${order.orderId} as delivered?`, onConfirm: handleMarkDelivered }) };
            default: return null;
        }
    })();

    const canCancel = ['pending', ORDER_STATUS.PENDING_APPROVAL, ORDER_STATUS.APPROVED,
        ORDER_STATUS.AWAITING_PAYMENT, ORDER_STATUS.IN_PRODUCTION, 'processing'].includes(order?.status);

    if (isLoading) return <section className="aod-page"><div className="aod-empty-state">Loading orders…</div></section>;
    if (!order)    return <section className="aod-page"><div className="aod-empty-state">No order selected.</div></section>;

    return (
        <section className="aod-page">
            {actionError && (
                <div className="aod-banner aod-banner--error" onClick={() => setActionError(null)}>
                    {actionError}
                </div>
            )}

            {/* ── PayMongo payment receipt modal ── */}
            {receiptModal && (
                <div className="aod-lightbox-overlay" onClick={() => setReceiptModal(null)}>
                    <div className="aod-lightbox" onClick={e => e.stopPropagation()}>
                        <div className="aod-lightbox-header">
                            <span className="aod-lightbox-label">Payment Receipt</span>
                            <button className="aod-lightbox-close" onClick={() => setReceiptModal(null)}>✕</button>
                        </div>
                        {receiptModal.loading && <p style={{ padding: '16px' }}>Loading payment receipt…</p>}
                        {!receiptModal.loading && receiptModal.error && (
                            <p style={{ padding: '16px', color: '#b00020' }}>{receiptModal.error}</p>
                        )}
                        {!receiptModal.loading && receiptModal.receipt && (
                            <div className="aod-info-rows" style={{ padding: '16px' }}>
                                <div className="aod-info-row"><span>Receipt #</span><strong>{receiptModal.receipt.receiptNumber || '—'}</strong></div>
                                <div className="aod-info-row"><span>Order</span><strong>{receiptModal.receipt.orderId || order?.orderId || '—'}</strong></div>
                                <div className="aod-info-row"><span>Amount</span><strong>{formatReceiptAmount(receiptModal.receipt.amount)}</strong></div>
                                <div className="aod-info-row"><span>Channel</span><strong>{receiptModal.receipt.paymentMethodName || 'PayMongo'}</strong></div>
                                <div className="aod-info-row"><span>Trace ID</span><strong>{receiptModal.receipt.traceId || '—'}</strong></div>
                                <div className="aod-info-row"><span>PayMongo ID</span><strong>{receiptModal.receipt.paymongoPaymentId || order?.paymongoPaymentId || '—'}</strong></div>
                                <div className="aod-info-row"><span>Email Status</span><strong>{receiptModal.receipt.deliveryStatus || order?.paymentReceiptStatus || 'queued'}</strong></div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── File lightbox ── */}
            {lightbox && (
                <div className="aod-lightbox-overlay" onClick={() => setLightbox(null)}>
                    <div className="aod-lightbox" onClick={e => e.stopPropagation()}>
                        <div className="aod-lightbox-header">
                            <span className="aod-lightbox-label">{lightbox.label}</span>
                            <div className="aod-lightbox-actions">
                                <button className="aod-lightbox-dl" onClick={() => handleDownload(lightbox.url, lightbox.label)}>↓ Download</button>
                                <button className="aod-lightbox-close" onClick={() => setLightbox(null)}>✕</button>
                            </div>
                        </div>
                        <img src={lightbox.url} alt={lightbox.label} />
                    </div>
                </div>
            )}

            {/* ── Confirm dialog ── */}
            {confirmDialog && (
                <div className="aod-dialog-overlay" onClick={() => !actionLoading && setConfirmDialog(null)}>
                    <div className="aod-dialog" onClick={e => e.stopPropagation()}>
                        <h3 className="aod-dialog-title">{confirmDialog.label}</h3>
                        {confirmDialog.type === 'cancel' && (
                            <div className="aod-dialog-body">
                                <label className="aod-dialog-label">Cancellation reason</label>
                                <div className="aod-cancel-reasons">
                                    {CANCELLATION_REASONS.map(r => (
                                        <label key={r} className="aod-reason-option">
                                            <input
                                                type="radio"
                                                name="cancelReason"
                                                value={r}
                                                checked={cancelReason === r}
                                                onChange={() => setCancelReason(r)}
                                            />
                                            {r}
                                        </label>
                                    ))}
                                </div>
                                {cancelReason === 'Other' && (
                                    <textarea
                                        className="aod-cancel-other"
                                        placeholder="Enter custom reason…"
                                        value={cancelOther}
                                        onChange={e => setCancelOther(e.target.value)}
                                    />
                                )}
                            </div>
                        )}
                        <div className="aod-dialog-actions">
                            <button
                                className="aod-dialog-btn aod-dialog-btn--secondary"
                                onClick={() => setConfirmDialog(null)}
                                disabled={actionLoading}>
                                Cancel
                            </button>
                            <button
                                className="aod-dialog-btn aod-dialog-btn--primary"
                                onClick={confirmDialog.type === 'cancel' ? handleCancel : confirmDialog.onConfirm}
                                disabled={actionLoading}>
                                {actionLoading ? 'Processing…' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── ShirtPreviewModal ── */}
            <ShirtPreviewModal
                open={previewModalOpen}
                onClose={() => setPreviewModalOpen(false)}
                designImages={previewItem?.designImages || {}}
                isCircle={(previewItem?.designShape || '').toLowerCase() === 'circle'}
                shirtColor={previewItem?.color || ''}
                title={previewItem ? `Design Preview — Item #${(previewItem.idx ?? 0) + 1}` : 'Design Preview'}
                hideBackButton={true}
            />

            {/* Navigator */}
            <div className="aod-navigator">
                <div className="aod-navigator-left">
                    <button className="aod-nav-btn" onClick={() => goToOrder(orders[currentIdx - 1])} disabled={!hasPrev}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5M5 12l7 7M5 12l7-7"/>
                        </svg>
                        Previous
                    </button>
                    <span className="aod-nav-indicator">{currentIdx > -1 ? `${currentIdx + 1} / ${orders.length}` : '—'}</span>
                    <button className="aod-nav-btn" onClick={() => goToOrder(orders[currentIdx + 1])} disabled={!hasNext}>
                        Next
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14M19 12l-7 7M19 12l-7-7"/>
                        </svg>
                    </button>
                </div>
                <div className="aod-navigator-right">
                    <span className="aod-nav-order-id">{order.orderId}</span>
                    <span className="aod-status-badge" style={{ background: sc.bg, color: sc.text }}>{sc.label}</span>
                </div>
            </div>

            {/* Metric cards */}
            <div className="aod-metrics">
                <article className="aod-metric-card">
                    <h3>Order ID</h3>
                    <p className="aod-metric-id">{order.orderId || '—'}</p>
                </article>
                <article className="aod-metric-card">
                    <h3>Status</h3>
                    <span className="aod-status-badge" style={{ background: sc.bg, color: sc.text }}>{sc.label}</span>
                    {expiryInfo && (
                        <p className={`aod-expiry-note ${expiryInfo.expired ? 'expired' : ''}`}>⏱ {expiryInfo.label}</p>
                    )}
                </article>
                <article className="aod-metric-card">
                    <h3>Date Ordered</h3>
                    <p>{formatDate(order.createdAt?.toDate ? order.createdAt.toDate() : order.createdAt || order.date)}</p>
                </article>
                <article className="aod-metric-card">
                    <h3>Order Total</h3>
                    <p className="aod-metric-total">₱ {payableOrderTotal.toLocaleString()}</p>
                </article>
            </div>

            {/* Main grid */}
            <div className="aod-content-grid">

                <section className="aod-card aod-summary">
                    <div className="aod-card-header">
                        <h2>Order Summary</h2>
                        <span className="aod-items-count">{orderItems.length} item{orderItems.length !== 1 ? 's' : ''}</span>
                    </div>

                    {orderItems.length === 0 && <div className="aod-empty-state">No item details found for this order.</div>}

                    {orderItems.map((item, idx) => {
                        const isPoloJersey = item.subcategory === 'Polo Shirt' || item.subcategory === 'Jersey';
                        const isJersey     = item.subcategory === 'Jersey';
                        const itemTotal    = calcItemTotal(item);
                        const shirtSub     = isPoloJersey ? itemTotal : calcShirtSubtotal(item);
                        const printSub     = isPoloJersey ? 0        : calcPrintSubtotal(item);
                        // Normalize quantities keys to lowercase for robust matching
                        const normalizedQuantities = Object.fromEntries(
                            Object.entries(item.quantities || {}).map(([k, v]) => [k.toLowerCase(), v])
                        );
                        const sizedQtys = SIZE_ORDER.filter(s => (normalizedQuantities[s] || 0) > 0);

                        return (
                            <div key={item.id || idx} className="aod-item-row">
                                <div className="aod-item-left">
                                    {item.image && <img src={item.image} alt={item.name} className="aod-item-thumb" />}
                                    <div className="aod-item-details">
                                        <h4>Item #{idx + 1} — {item.name || 'Custom Item'}</h4>

                                        {/* ── Design email notice banner (T-Shirt only) ── */}
                                        <DesignEmailNoticeBanner
                                            item={item}
                                            customerEmail={customerEmail !== '—' ? customerEmail : order?.customerEmail || ''}
                                        />

                                        <div className="aod-item-specs">
                                            <span><strong>Type:</strong> {item.type || '—'}</span>

                                            {/* Color, Design Shape, Print Location: hidden for Polo/Jersey */}
                                            {!isPoloJersey && (
                                                <>
                                                    <span><strong>Color:</strong> {item.color || '—'}</span>
                                                    <span><strong>Design Shape:</strong> {item.designShape || '—'}</span>
                                                    <span><strong>Print Location:</strong> {item.printLocation || '—'}</span>
                                                </>
                                            )}

                                            {/* Jersey-specific fields */}
                                            {isJersey && (
                                                <>
                                                    {item.teamName && <span><strong>Team Member:</strong> {item.teamName}</span>}
                                                    {item.surname  && <span><strong>Surname:</strong> {item.surname}</span>}
                                                    {item.number   && <span><strong>Number:</strong> {item.number}</span>}
                                                </>
                                            )}
                                        </div>

                                        {sizedQtys.length > 0 && (
                                            <div className="aod-size-table-wrap">
                                                <table className="aod-size-table">
                                                    <thead>
                                                        <tr>
                                                            {sizedQtys.map(s => <th key={s}>{SIZE_LABELS[s] || s.toUpperCase()}</th>)}
                                                            <th>Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            {sizedQtys.map(s => <td key={s}>{normalizedQuantities[s]}</td>)}  
                                                            <td><strong>{item.quantity || 0}</strong></td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        <div className="aod-item-price-breakdown">
                                            <span>
                                                {isPoloJersey ? 'Subtotal' : 'Shirt subtotal'}:{' '}
                                                <strong>₱ {shirtSub.toLocaleString()}</strong>
                                            </span>
                                            {/* FIX: Show color surcharge note for T-shirts */}
                                            {!isPoloJersey && Number(item.colorSurcharge || 0) > 0 && (
                                                <span style={{ fontSize: '12px', color: '#666' }}>
                                                    (includes ₱{item.colorSurcharge}/pc color surcharge)
                                                </span>
                                            )}
                                            {printSub > 0 && <span>Print subtotal: <strong>₱ {printSub.toLocaleString()}</strong></span>}
                                            <span className="aod-item-total-line">Item total: <strong>₱ {itemTotal.toLocaleString()}</strong></span>
                                        </div>
                                        {item.note && <p className="aod-item-note">Note: {item.note}</p>}
                                    </div>
                                </div>

                                <div className="aod-design-actions">
                                    <p className="aod-design-label">Design Files</p>

                                    {/* FIX: Polo/Jersey — design was made by JG Printing */}
                                    {isPoloJersey ? (
                                        <span className="aod-no-design" style={{ fontStyle: 'italic', color: '#555' }}>
                                            Design was made by JG Printing
                                        </span>
                                    ) : (
                                        <>
                                            {item.designImages && Object.keys(item.designImages).length > 0 && (
                                                <button
                                                    type="button"
                                                    className="aod-design-btn"
                                                    style={{ background:'#22314a', color:'#fff', marginBottom:'8px', justifyContent:'center' }}
                                                    onClick={() => openDesignPreview({ ...item, idx })}
                                                >
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                                        <circle cx="12" cy="12" r="3"/>
                                                    </svg>
                                                    <span className="aod-design-btn-label">Preview All</span>
                                                </button>
                                            )}
                                            {item.designImages && Object.keys(item.designImages).length > 0
                                                ? Object.entries(item.designImages)
                                                    .filter(([, url]) => url && typeof url === 'string')
                                                    .map(([loc, url]) => (
                                                        <button key={loc} type="button" className="aod-design-btn" onClick={() => setLightbox({ url, label: loc })}>
                                                            <img src={url} alt={loc} className="aod-design-thumb-img" />
                                                            <span className="aod-design-btn-label">{loc}</span>
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="22" y2="22"/>
                                                            </svg>
                                                        </button>
                                                    ))
                                                : <span className="aod-no-design">No designs uploaded</span>
                                            }
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {orderItems.length > 0 && (
                        <>
                            <div className="aod-grand-total-row">
                                <span>Subtotal</span>
                                <strong>₱ {itemsSubtotal.toLocaleString()}</strong>
                            </div>
                            {deliveryFee > 0 && (
                                <div className="aod-grand-total-row">
                                    <span>Delivery Fee</span>
                                    <strong>₱ {deliveryFee.toLocaleString()}</strong>
                                </div>
                            )}
                            {discountAmount > 0 && (
                                <div className="aod-grand-total-row">
                                    <span>{order?.promoCode ? `Promo Discount (${order.promoCode})` : 'Promo Discount'}</span>
                                    <strong>-₱ {discountAmount.toLocaleString()}</strong>
                                </div>
                            )}
                            <div className="aod-grand-total-row">
                                <span>Grand Total</span>
                                <strong>₱ {payableOrderTotal.toLocaleString()}</strong>
                            </div>
                        </>
                    )}
                </section>

                <aside className="aod-side-panel">
                    <section className="aod-card">
                        <h2>Customer</h2>
                        <div className="aod-info-rows">
                            <div className="aod-info-row"><span>Name</span><strong>{order.customerName || '—'}</strong></div>
                            <div className="aod-info-row"><span>Email</span><strong>{customerEmail}</strong></div>
                            <div className="aod-info-row"><span>Phone</span><strong>{customerPhone}</strong></div>
                        </div>
                        <button className="aod-message-btn" onClick={handleMessageCustomer}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                            </svg>
                            Message Customer
                        </button>
                    </section>

                    <section className="aod-card">
                        <h2>Delivery Details</h2>
                        <div className="aod-info-rows" style={{ marginBottom: '16px' }}>
                            <div className="aod-info-row">
                                <span>Method</span>
                                <strong>{order?.deliveryMethod || 'Not Specified'}</strong>
                            </div>
                        </div>
                        <h2 style={{ fontSize: '0.9rem', color: '#555' }}>Address</h2>
                        <p className="aod-address">{deliveryAddress}</p>
                    </section>

                    <section className="aod-card">
                        <h2>Payment</h2>
                        <div className="aod-info-rows">
                            <div className="aod-info-row"><span>Gateway</span><strong>{order.paymentGateway || 'PayMongo'}</strong></div>
                            <div className="aod-info-row"><span>Status</span><strong style={{ color: paymongoPaid ? '#2d7a3a' : '#b07c00' }}>{formatPaymongoPaymentStatus(order)}</strong></div>
                            <div className="aod-info-row">
                                <span>Receipt</span>
                                {canViewReceipt
                                    ? <button type="button" className="aod-view-receipt" onClick={handleViewPaymentReceipt}>View Receipt</button>
                                    : <strong>—</strong>}
                            </div>
                            {order.paymentReceiptNumber && (
                                <div className="aod-info-row"><span>Receipt #</span><strong>{order.paymentReceiptNumber}</strong></div>
                            )}
                            {order.paymentTraceId && (
                                <div className="aod-info-row"><span>Trace ID</span><strong>{order.paymentTraceId}</strong></div>
                            )}
                            {order.paymongoPaymentMethod && (
                                <div className="aod-info-row"><span>Paid via</span><strong>{order.paymongoPaymentMethod}</strong></div>
                            )}
                            {order.paymongoPaymentId && (
                                <div className="aod-info-row"><span>PayMongo ID</span><strong>{order.paymongoPaymentId}</strong></div>
                            )}
                            {order.paymongoCheckoutUrl && order.status === ORDER_STATUS.AWAITING_PAYMENT && (
                                <div className="aod-info-row">
                                    <span>Checkout</span>
                                    <a href={order.paymongoCheckoutUrl} target="_blank" rel="noreferrer" className="aod-view-receipt">Open PayMongo Link</a>
                                </div>
                            )}
                        </div>
                    </section>

                    {order.status === ORDER_STATUS.CANCELLED && order.cancellationReason && (
                        <section className="aod-card aod-cancelled-card">
                            <h2>Cancellation Reason</h2>
                            <p>{order.cancellationReason}</p>
                        </section>
                    )}

                    {order.approvedBy?.name && (
                        <section className="aod-card">
                            <h2>Approval</h2>
                            <div className="aod-info-rows">
                                <div className="aod-info-row"><span>Approved by</span><strong>{order.approvedBy.name}</strong></div>
                                <div className="aod-info-row"><span>Role</span><strong>{getRoleLabel(order.approvedBy.role)}</strong></div>
                                <div className="aod-info-row"><span>Date</span><strong>{formatDateTime(order.approvedAt)}</strong></div>
                            </div>
                        </section>
                    )}

                    {order.statusHistory?.length > 0 && (
                        <section className="aod-card">
                            <h2>Status History</h2>
                            <div className="aod-status-history">
                                {[...order.statusHistory].reverse().map((entry, i) => {
                                    const cfg = STATUS_CONFIG[entry.status] || { bg: '#eee', text: '#555', label: entry.status };
                                    return (
                                        <div key={i} className="aod-history-entry">
                                            <span className="aod-history-badge" style={{ background: cfg.bg, color: cfg.text }}>{cfg.label}</span>
                                            <div className="aod-history-meta">
                                                <span>{formatDateTime(entry.timestamp)}</span>
                                                {entry.note && <span className="aod-history-note">{entry.note}</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    <div className="aod-action-panel">
                        {[ORDER_STATUS.IN_PRODUCTION, 'processing'].includes(order?.status) && (
                            <section className="aod-card">
                                <h2>Estimated Date to Be Delivered</h2>
                                <input
                                    type="date"
                                    value={estimatedDeliveryDate || order?.estimatedDeliveryDate || ''}
                                    onChange={e => setEstimatedDeliveryDate(e.target.value)}
                                    style={{ width:'100%', padding:'8px 12px', borderRadius:'6px', border:'1px solid #ddd', fontSize:'14px' }}
                                />
                                {(estimatedDeliveryDate && estimatedDeliveryDate !== order?.estimatedDeliveryDate) && (
                                    <button
                                        style={{ marginTop:'8px', padding:'6px 14px', borderRadius:'6px', background:'#22314a', color:'#fff', border:'none', cursor:'pointer', fontSize:'13px', display:'block', margin:'8px auto 0' }}
                                        onClick={async () => {
                                            if (!order?.firestoreId) return;
                                            setActionLoading(true);
                                            const orderRef = doc(db, 'orders', order.firestoreId);
                                            await updateDoc(orderRef, { estimatedDeliveryDate });
                                            const userId = getOrderUserId(order);
                                            if (userId) {
                                                await writeNotification({
                                                    userId,
                                                    orderId:    order.orderId,
                                                    title:      'Estimated To Be Delivered Date Updated',
                                                    message:    `The estimated delivery date for your order ${order.orderId} has been updated by our team.`,
                                                    status:     'date_updated',
                                                    orderTotal: payableOrderTotal,
                                                });
                                            }
                                            setActionLoading(false);
                                            applyStatusUpdate(order.status, { estimatedDeliveryDate });
                                        }}
                                        disabled={actionLoading}
                                    >
                                        {actionLoading ? 'Saving…' : 'Save Date'}
                                    </button>
                                )}
                            </section>
                        )}
                        {actionBtn && (
                            <button
                                className="aod-primary-btn"
                                style={{ background: actionBtn.color }}
                                onClick={actionBtn.onClick}
                                disabled={actionLoading || actionBtn.disabled}>
                                {actionLoading ? 'Processing…' : actionBtn.label}
                            </button>
                        )}
                        {canCancel && (
                            <button
                                className="aod-cancel-order-btn"
                                onClick={() => setConfirmDialog({
                                    type: 'cancel',
                                    label: `Cancel order ${order.orderId}?`,
                                    onConfirm: handleCancel,
                                })}
                                disabled={actionLoading}>
                                Cancel Order
                            </button>
                        )}
                    </div>
                </aside>
            </div>
        </section>
    );
}